import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Header from './components/Layout/Header';
import Footer from './components/Footer';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AddSubscription from './pages/AddSubscription';
import Calendar from './pages/Calendar';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import TestDashboard from './pages/TestDashboard';
import AllSubscriptions from './pages/AllSubscriptions';
import NotFound from './pages/NotFound';
import ComingSoon from './pages/ComingSoon';

// Components
import AuthCallback from './components/AuthCallback';

// Loading component (unused but kept for future use)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LoadingSpinner: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <div>Loading...</div>
  </Box>
);

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  // Temporary bypass for testing
  const isTestMode = window.location.search.includes('test=true');
  
  if (isTestMode) {
    return <>{children}</>;
  }

  // Show loading for a longer time to prevent rapid refreshes
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
        }}
      >
        <div>Loading authentication...</div>
        <div style={{ marginTop: '10px', fontSize: '12px' }}>
          User: {user?.email || 'None'}, Loading: {loading.toString()}
        </div>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Main app layout
const AppLayout: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();

  // Update exchange rates on app start
  useEffect(() => {
    const updateRates = async () => {
      try {
        const { updateExchangeRatesInBackground } = await import('./utils/currency');
        await updateExchangeRatesInBackground();
      } catch (error) {
        console.error('Failed to update exchange rates:', error);
      }
    };
    
    updateRates();
    
    // Update rates every 30 minutes
    const interval = setInterval(updateRates, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#32CD32', // Lime Green
      },
      secondary: {
        main: '#20B2AA', // Light Sea Green (Teal)
      },
      info: {
        main: '#87CEEB', // Sky Blue
      },
      warning: {
        main: '#FF8C00', // Dark Orange
      },
      success: {
        main: '#32CD32', // Lime Green
      },
      error: {
        main: '#FF6347', // Tomato Red
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            zIndex: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C00 50%, #32CD32 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(50, 205, 50, 0.3)',
              color: 'white',
              transition: 'all 0.3s ease-in-out, background 3s ease-in-out',
              '& > *': {
                zIndex: 10,
                position: 'relative',
              },
              '& .MuiButton-label': {
                zIndex: 10,
                position: 'relative',
              },
              '& .MuiButton-startIcon': {
                zIndex: 10,
                position: 'relative',
              },
              '& .MuiButton-endIcon': {
                zIndex: 10,
                position: 'relative',
              },
              '&:active': {
                '&::before': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  width: '200px',
                  height: '200px',
                  zIndex: 5,
                },
              },
            },
            '&:active': {
              '&::before': {
                background: 'rgba(255, 255, 255, 0.2)',
                width: '300px',
                height: '300px',
                zIndex: 5,
              },
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '0',
              height: '0',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 107, 53) 0%, rgba(255, 140, 0) 30%, rgba(50, 205, 50) 100%)',
              transform: 'translate(-50%, -50%)',
              transition: 'width 0.6s, height 0.6s',
              zIndex: -1,
            },
            '&:hover::before': {
              width: '200px',
              height: '200px',
            },
            '&:active::before': {
              width: '200px',
              height: '200px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 4px 15px rgba(50, 205, 50, 0.2)',
            },
          },
        },
      },
    },
  });

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {location.pathname !== '/login' && location.pathname !== '/auth/callback' && (
          <Header toggleTheme={toggleTheme} isDarkMode={isDarkMode} />
        )}
        <Box component="main" sx={{ flexGrow: 1, pt: location.pathname === '/login' || location.pathname === '/auth/callback' ? 0 : 8 }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/test" element={<TestDashboard />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-subscription"
              element={
                <ProtectedRoute>
                  <AddSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/all-subscriptions"
              element={
                <ProtectedRoute>
                  <AllSubscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
        {location.pathname !== '/login' && location.pathname !== '/auth/callback' && (
          <Footer />
        )}
      </Box>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
};

export default App; 